import React, { useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { Text, View, XStack, YStack } from 'smoothie';

import type { Account, SimulationResult } from '../types';

function formatCents(cents: number): string {
    const abs = Math.abs(cents);
    const dollars = Math.floor(abs / 100);
    const formatted = dollars.toLocaleString('en-US');
    return cents < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatMonth(date: Date): string {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
    });
}

function getCellColor(
    account: Account,
    balance: number,
    prevBalance: number | undefined,
): string {
    if (account.type === 'DEBT') {
        if (balance === 0) return '#dcfce7';
        return balance < (prevBalance ?? balance) ? '#dcfce7' : '#fef9c3';
    }
    if (balance > (prevBalance ?? 0)) return '#dcfce7';
    if (balance < (prevBalance ?? 0)) return '#fee2e2';
    return 'transparent';
}

interface SimulationResultsTableProps {
    result: SimulationResult;
}

const COL_WIDTH = 120;
const LABEL_COL_WIDTH = 80;

export const SimulationResultsTable = ({
    result,
}: SimulationResultsTableProps) => {
    const accounts = result.moneyMap.accounts;
    const debtAccounts = useMemo(
        () => accounts.filter((a) => a.type === 'DEBT'),
        [accounts],
    );

    const ruleColumns = useMemo(() => {
        const cols: Array<{
            ruleId: string;
            label: string;
            ruleSetName: string;
        }> = [];
        const sortedSets = [...result.moneyMap.ruleSets].sort(
            (a, b) => a.priority - b.priority,
        );
        for (const rs of sortedSets) {
            const sortedRules = [...rs.rules].sort(
                (a, b) => a.priority - b.priority,
            );
            for (const rule of sortedRules) {
                cols.push({
                    ruleId: rule.id,
                    label: rule.description,
                    ruleSetName: rs.name,
                });
            }
        }
        return cols;
    }, [result.moneyMap.ruleSets]);

    const ruleSetGroups = useMemo(() => {
        const groups: Array<{ name: string; count: number }> = [];
        let currentName = '';
        let currentCount = 0;
        for (const col of ruleColumns) {
            if (col.ruleSetName !== currentName) {
                if (currentCount > 0) {
                    groups.push({ name: currentName, count: currentCount });
                }
                currentName = col.ruleSetName;
                currentCount = 1;
            } else {
                currentCount++;
            }
        }
        if (currentCount > 0) {
            groups.push({ name: currentName, count: currentCount });
        }
        return groups;
    }, [ruleColumns]);

    return (
        <YStack gap={16}>
            <Text fontSize={18} fontWeight="700">
                Simulation Results ({result.months.length} months)
            </Text>

            <ScrollView
                horizontal
                style={styles.scrollContainer}
                showsHorizontalScrollIndicator
            >
                <YStack>
                    {/* Rule set group header */}
                    <XStack>
                        <View
                            style={[
                                styles.headerCell,
                                { width: LABEL_COL_WIDTH },
                            ]}
                        >
                            <Text style={styles.headerText}>Month</Text>
                        </View>
                        <View
                            style={[
                                styles.groupHeaderCell,
                                {
                                    width: accounts.length * COL_WIDTH,
                                    backgroundColor: '#e0e7ff',
                                },
                            ]}
                        >
                            <Text style={styles.groupHeaderText}>
                                Starting Balances
                            </Text>
                        </View>
                        <View
                            style={[
                                styles.groupHeaderCell,
                                {
                                    width: accounts.length * COL_WIDTH,
                                    backgroundColor: '#fef3c7',
                                },
                            ]}
                        >
                            <Text style={styles.groupHeaderText}>
                                Interest / Growth
                            </Text>
                        </View>
                        {debtAccounts.length > 0 && (
                            <View
                                style={[
                                    styles.groupHeaderCell,
                                    {
                                        width: debtAccounts.length * COL_WIDTH,
                                        backgroundColor: '#fce7f3',
                                    },
                                ]}
                            >
                                <Text style={styles.groupHeaderText}>
                                    Minimums (Bills)
                                </Text>
                            </View>
                        )}
                        {ruleSetGroups.map((group) => (
                            <View
                                key={group.name}
                                style={[
                                    styles.groupHeaderCell,
                                    {
                                        width: group.count * COL_WIDTH,
                                        backgroundColor: '#dbeafe',
                                    },
                                ]}
                            >
                                <Text style={styles.groupHeaderText}>
                                    {group.name}
                                </Text>
                            </View>
                        ))}
                        <View
                            style={[
                                styles.groupHeaderCell,
                                {
                                    width: COL_WIDTH,
                                    backgroundColor: '#f3e8ff',
                                },
                            ]}
                        >
                            <Text style={styles.groupHeaderText}>Leftover</Text>
                        </View>
                        <View
                            style={[
                                styles.groupHeaderCell,
                                {
                                    width: accounts.length * COL_WIDTH,
                                    backgroundColor: '#d1fae5',
                                },
                            ]}
                        >
                            <Text style={styles.groupHeaderText}>
                                Ending Balances
                            </Text>
                        </View>
                    </XStack>

                    {/* Column header row */}
                    <XStack>
                        <View
                            style={[
                                styles.headerCell,
                                { width: LABEL_COL_WIDTH },
                            ]}
                        >
                            <Text style={styles.subHeaderText}> </Text>
                        </View>
                        {accounts.map((a) => (
                            <View
                                key={`start-${a.id}`}
                                style={[
                                    styles.headerCell,
                                    { width: COL_WIDTH },
                                ]}
                            >
                                <Text
                                    style={styles.subHeaderText}
                                    numberOfLines={2}
                                >
                                    {a.name}
                                </Text>
                            </View>
                        ))}
                        {accounts.map((a) => (
                            <View
                                key={`int-${a.id}`}
                                style={[
                                    styles.headerCell,
                                    { width: COL_WIDTH },
                                ]}
                            >
                                <Text
                                    style={styles.subHeaderText}
                                    numberOfLines={2}
                                >
                                    {a.name}
                                </Text>
                            </View>
                        ))}
                        {debtAccounts.map((a) => (
                            <View
                                key={`min-${a.id}`}
                                style={[
                                    styles.headerCell,
                                    { width: COL_WIDTH },
                                ]}
                            >
                                <Text
                                    style={styles.subHeaderText}
                                    numberOfLines={2}
                                >
                                    {a.name}
                                </Text>
                            </View>
                        ))}
                        {ruleColumns.map((col) => (
                            <View
                                key={`rule-${col.ruleId}`}
                                style={[
                                    styles.headerCell,
                                    { width: COL_WIDTH },
                                ]}
                            >
                                <Text
                                    style={styles.subHeaderText}
                                    numberOfLines={2}
                                >
                                    {col.label}
                                </Text>
                            </View>
                        ))}
                        <View style={[styles.headerCell, { width: COL_WIDTH }]}>
                            <Text style={styles.subHeaderText}>Remaining</Text>
                        </View>
                        {accounts.map((a) => (
                            <View
                                key={`end-${a.id}`}
                                style={[
                                    styles.headerCell,
                                    { width: COL_WIDTH },
                                ]}
                            >
                                <Text
                                    style={styles.subHeaderText}
                                    numberOfLines={2}
                                >
                                    {a.name}
                                </Text>
                            </View>
                        ))}
                    </XStack>

                    {/* Data rows */}
                    <ScrollView style={styles.bodyScroll}>
                        {result.months.map((month, mi) => {
                            const prevMonth =
                                mi > 0 ? result.months[mi - 1] : undefined;
                            const hasFreed =
                                month.freedFromPaidOffDebtsCents > 0;
                            return (
                                <XStack
                                    key={month.monthIndex}
                                    backgroundColor={
                                        hasFreed
                                            ? '#ecfdf5'
                                            : mi % 2 === 0
                                              ? '$white'
                                              : '#f9fafb'
                                    }
                                >
                                    <View
                                        style={[
                                            styles.cell,
                                            {
                                                width: LABEL_COL_WIDTH,
                                                backgroundColor: '#f3f4f6',
                                            },
                                        ]}
                                    >
                                        <Text style={styles.cellTextBold}>
                                            {formatMonth(month.date)}
                                        </Text>
                                    </View>

                                    {accounts.map((a) => (
                                        <View
                                            key={`start-${a.id}`}
                                            style={[
                                                styles.cell,
                                                {
                                                    width: COL_WIDTH,
                                                },
                                            ]}
                                        >
                                            <Text style={styles.cellText}>
                                                {formatCents(
                                                    month.balancesBefore[
                                                        a.id
                                                    ] ?? 0,
                                                )}
                                            </Text>
                                        </View>
                                    ))}

                                    {accounts.map((a) => {
                                        const interest =
                                            month.interestApplied[a.id] ?? 0;
                                        return (
                                            <View
                                                key={`int-${a.id}`}
                                                style={[
                                                    styles.cell,
                                                    {
                                                        width: COL_WIDTH,
                                                        backgroundColor:
                                                            interest !== 0
                                                                ? a.type ===
                                                                  'DEBT'
                                                                    ? '#fee2e2'
                                                                    : '#dcfce7'
                                                                : 'transparent',
                                                    },
                                                ]}
                                            >
                                                <Text style={styles.cellText}>
                                                    {interest !== 0
                                                        ? formatCents(interest)
                                                        : '-'}
                                                </Text>
                                            </View>
                                        );
                                    })}

                                    {debtAccounts.map((a) => {
                                        const minPaid =
                                            month.minimumPaymentsApplied[
                                                a.id
                                            ] ?? 0;
                                        return (
                                            <View
                                                key={`min-${a.id}`}
                                                style={[
                                                    styles.cell,
                                                    {
                                                        width: COL_WIDTH,
                                                        backgroundColor:
                                                            minPaid > 0
                                                                ? '#fce7f3'
                                                                : 'transparent',
                                                    },
                                                ]}
                                            >
                                                <Text style={styles.cellText}>
                                                    {minPaid > 0
                                                        ? formatCents(minPaid)
                                                        : '-'}
                                                </Text>
                                            </View>
                                        );
                                    })}

                                    {ruleColumns.map((col) => {
                                        const transfer = month.transfers.find(
                                            (t) => t.ruleId === col.ruleId,
                                        );
                                        return (
                                            <View
                                                key={`rule-${col.ruleId}`}
                                                style={[
                                                    styles.cell,
                                                    {
                                                        width: COL_WIDTH,
                                                        backgroundColor:
                                                            transfer &&
                                                            transfer.amountCents >
                                                                0
                                                                ? '#dbeafe'
                                                                : 'transparent',
                                                    },
                                                ]}
                                            >
                                                <Text style={styles.cellText}>
                                                    {transfer
                                                        ? formatCents(
                                                              transfer.amountCents,
                                                          )
                                                        : '-'}
                                                </Text>
                                            </View>
                                        );
                                    })}

                                    <View
                                        style={[
                                            styles.cell,
                                            {
                                                width: COL_WIDTH,
                                                backgroundColor:
                                                    month.leftoverCents > 0
                                                        ? '#f3e8ff'
                                                        : 'transparent',
                                            },
                                        ]}
                                    >
                                        <Text style={styles.cellText}>
                                            {formatCents(month.leftoverCents)}
                                        </Text>
                                        {hasFreed && (
                                            <Text
                                                fontSize={9}
                                                color="#059669"
                                                fontWeight="600"
                                            >
                                                +
                                                {formatCents(
                                                    month.freedFromPaidOffDebtsCents,
                                                )}{' '}
                                                freed
                                            </Text>
                                        )}
                                    </View>

                                    {accounts.map((a) => {
                                        const balance =
                                            month.balancesAfter[a.id] ?? 0;
                                        const prev =
                                            prevMonth?.balancesAfter[a.id];
                                        return (
                                            <View
                                                key={`end-${a.id}`}
                                                style={[
                                                    styles.cell,
                                                    {
                                                        width: COL_WIDTH,
                                                        backgroundColor:
                                                            getCellColor(
                                                                a,
                                                                balance,
                                                                prev,
                                                            ),
                                                    },
                                                ]}
                                            >
                                                <Text
                                                    style={[
                                                        styles.cellText,
                                                        styles.cellTextBold,
                                                    ]}
                                                >
                                                    {formatCents(balance)}
                                                </Text>
                                            </View>
                                        );
                                    })}
                                </XStack>
                            );
                        })}
                    </ScrollView>
                </YStack>
            </ScrollView>
        </YStack>
    );
};

const styles = StyleSheet.create({
    scrollContainer: {
        flex: 1,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        borderRadius: 8,
    },
    groupHeaderCell: {
        paddingHorizontal: 4,
        paddingVertical: 6,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#d1d5db',
        alignItems: 'center',
        justifyContent: 'center',
    },
    groupHeaderText: {
        fontSize: 11,
        fontWeight: '700',
        textAlign: 'center',
    },
    headerCell: {
        paddingHorizontal: 6,
        paddingVertical: 4,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#d1d5db',
        backgroundColor: '#f9fafb',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerText: {
        fontSize: 11,
        fontWeight: '700',
    },
    subHeaderText: {
        fontSize: 10,
        fontWeight: '600',
        textAlign: 'center',
    },
    bodyScroll: {
        maxHeight: 600,
    },
    cell: {
        paddingHorizontal: 6,
        paddingVertical: 4,
        borderRightWidth: StyleSheet.hairlineWidth,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderColor: '#e5e7eb',
        alignItems: 'flex-end',
        justifyContent: 'center',
    },
    cellText: {
        fontSize: 11,
        fontFamily: 'monospace',
    },
    cellTextBold: {
        fontWeight: '600',
    },
});
